/* eslint formatjs/no-literal-string-in-jsx: "error" */
import useUpdateThread from "./hooks/useUpdateThread"
import { useMaxMessageLength } from "./utils"
import {
  Container,
  FlexContainer,
  Form,
  FormTextarea,
  useBreakpointComponent,
  useFormContext
} from "@carrotfertility/carotene"
import { FormSendButton, FormSendIconButton } from "./InAppMessagingFormButtons"
import React, { RefObject, useEffect, useState } from "react"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import { InAppMessagingThread } from "./inAppMessagingTypes"
import useCreateThread from "./hooks/useCreateThread"
import useGetThreads from "./hooks/useGetThreads"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import { useIntl } from "react-intl"

type MessageReplyContainerProps = {
  thread: InAppMessagingThread
  scrollThreadsListToTop: () => void
}

const SendButtonContainer = ({
  handleMargin,
  setMargin,
  textAreaRef
}: {
  handleMargin: () => number
  setMargin: React.Dispatch<number>
  margin: number
  textAreaRef: RefObject<HTMLDivElement>
}): JSX.Element => {
  const instance = useFormContext()
  const isError = !instance?.meta?.canSubmit || instance?.values.message === "" || instance?.values.subject === ""

  useEffect(() => {
    // this solution is a bit clunky, but it seems to be effective for ensuring the 'send' button is positioned correctly
    // forces the update of the margin height based on the form validation and the height of the textAreaRef
    if (isError || textAreaRef) {
      const helperText = textAreaRef.current.firstChild.lastChild as HTMLElement
      setMargin(helperText.clientHeight)
    }
  })

  const sendButton = useBreakpointComponent([
    <FormSendIconButton key="smallest" />,
    <FormSendIconButton key="small" />,
    <FormSendButton key="med" />,
    <FormSendButton key="large" />
  ])

  return (
    <Container
      height="56px"
      paddingTopBottom="tiny"
      sx={{
        marginBottom: handleMargin
      }}
    >
      {sendButton}
    </Container>
  )
}

export function MessageReplyContainer({ thread, scrollThreadsListToTop }: MessageReplyContainerProps): JSX.Element {
  const { mutateAsync: updateThreadAsync } = useUpdateThread()
  const { mutateAsync: createThreadAsync } = useCreateThread()
  const { refetch: refetchThreads } = useGetThreads()
  const { validateMaxLength, maxLength } = useMaxMessageLength({ maxLength: 1600 })
  const { setThreadIdForFailedUpdate, setThreadUpdateInFlight, setSelectedThread } = useInAppMessaging()
  const [margin, setMargin] = useState(21)
  const intl = useIntl()
  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth
  })

  // this is a workaround to capture the div that surrounds the helper text message on the Carotene Textarea component
  const textAreaRef = React.useRef(null)
  const helperTextHeight = textAreaRef?.current?.firstChild?.lastChild?.clientHeight

  React.useEffect(() => {
    //these event listeners ensure that the 'send' button gets repositioned correctly when the window is resized
    function handleResize(): void {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth
      })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [dimensions])

  function handleMargin(): number {
    if (helperTextHeight) {
      setMargin(helperTextHeight)
    }
    // the margin under the 'send' button should always be at least 21 pixels
    return margin > 0 ? margin : 21
  }

  async function onSubmit(values, instance): Promise<void> {
    setThreadUpdateInFlight(true)

    if (thread.isClosed) {
      values.viaFollowUpSourceId = thread.threadId
      values.subject = intl
        .formatMessage({ defaultMessage: "Re: {subject}" }, { subject: thread.subject })
        .substring(0, 150)

      try {
        const followupThread = await createThreadAsync(values)
        await refetchThreads()
        setSelectedThread(followupThread)
        setThreadIdForFailedUpdate(null)
        setThreadUpdateInFlight(false)
        scrollThreadsListToTop()
        instance.reset()
      } catch (error) {
        reportErrorMessage(`Unable to create follow up thread for thread id: ${thread.threadId}. Error: ${error}`)
        setThreadIdForFailedUpdate(thread.threadId)
        setThreadUpdateInFlight(false)
      }
    } else {
      values.threadId = thread.threadId

      try {
        await updateThreadAsync(values)
        instance.reset()
        setThreadIdForFailedUpdate(null)
        setThreadUpdateInFlight(false)
      } catch (error) {
        reportErrorMessage(`Unable to update or reply to thread id: ${thread.threadId}. Error: ${error}`)
        setThreadIdForFailedUpdate(thread.threadId)
        setThreadUpdateInFlight(false)
      }
    }
  }

  return (
    <Container paddingLeftRight="small">
      <Form onSubmit={onSubmit}>
        <FlexContainer className="input-group" height="auto" minHeight="56px" alignItems="end">
          <Container
            height="auto"
            width={["100%", null, "488px", null]}
            sx={{ marginRight: ["8px", null, "12px", null] }}
          >
            <div ref={textAreaRef} id="text-container">
              <FormTextarea
                name="message"
                placeholder={intl.formatMessage({ defaultMessage: "Write a message..." })}
                validate={(value) => {
                  validateMaxLength(value)
                  return value.trim() ? false : intl.formatMessage({ defaultMessage: "Required" })
                }}
                maxLength={maxLength}
                minRows={1}
                maxRows={9}
                autosize
                helperMessage={intl.formatMessage({ defaultMessage: "We usually respond within one business day." })}
              />
            </div>
          </Container>
          <SendButtonContainer
            textAreaRef={textAreaRef}
            setMargin={setMargin}
            margin={margin}
            handleMargin={handleMargin}
          />
        </FlexContainer>
      </Form>
    </Container>
  )
}
